import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, TextField } from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { VySwitch } from '../../../../../../components/new-design/inputs/VySwitchNew';
import { VyDialog } from '../../../../../../components/new-design/feedback/VyDialog';
import { withSuspenseBoundary } from '../../../../../../components/utility/SuspenseBoundary';
import { isNotNullOrUndefined } from '@vy/utils';
import type { CreditType } from '@vy/api-definition';
import { UpsertCreditTypeFormSchema, type UpsertCreditTypeFormData } from './upsert-credit-type-form-schema';
import { useCreditTypeSubmission } from './hooks/use-credit-type-submission';

type UpsertCreditTypeDialogProps = {
  openCreateProductDialog: boolean;
  onClose: () => void;
  onProductCreated?: (productName: string) => void;
  onCreditTypeCreated?: (creditType: CreditType) => void;
  onBackToMain?: () => void;
  autoOpen?: boolean;
  editCreditType?: CreditType;
};

const UpsertCreditTypeDialog: React.FC<UpsertCreditTypeDialogProps> = ({
  openCreateProductDialog,
  onClose,
  onProductCreated,
  onCreditTypeCreated,
  onBackToMain,
  editCreditType,
}) => {
  const isEditMode = isNotNullOrUndefined(editCreditType);

  const {
    handleSubmit: formHandleSubmit,
    register,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm<UpsertCreditTypeFormData>({
    resolver: zodResolver(UpsertCreditTypeFormSchema),
    mode: 'onChange',
    defaultValues: editCreditType ? { name: editCreditType.name, units: editCreditType.units, isTaxable: editCreditType.isTaxable ?? true } : undefined,
  });

  const { submitCreditType } = useCreditTypeSubmission({
    onSuccess: onProductCreated,
    onCreditTypeCreated,
    onClose,
    resetCallerState: reset,
    editCreditType,
  });

  const handleSubmit = formHandleSubmit(submitCreditType);

  const handleDialogClose = () => {
    reset();
    onClose();
  };

  return (
    <VyDialog
      open={openCreateProductDialog}
      onClose={handleDialogClose}
      label={isEditMode ? 'Edit credit product' : 'Credit product'}
      ctaText={isEditMode ? 'Update product' : 'Create product'}
      ctaDisabled={!isValid}
      cancelButtonText="Cancel"
      disableDividers
      onSubmit={handleSubmit}
      maxWidth="md"
      navigateBack={onBackToMain}
      ctaButtonProps={{
        sx: {
          fontSize: '16px',
          width: '166px',
          fontWeight: 500,
          lineHeight: '24px',
          color: '#FFF',
          backgroundColor: '#0D0D48',
          '&:hover': {
            backgroundColor: '#0D0D48',
          },
        },
      }}
    >
      <Stack spacing={3}>
        <TextField
          label="Product name"
          placeholder="Enter product name"
          fullWidth
          size="medium"
          data-vy="upsert-credit-type-name-input"
          {...register('name')}
          error={errors.name !== undefined}
          helperText={errors.name?.message}
        />
        <TextField
          label="Units"
          placeholder="Enter units (e.g. credits, tokens)"
          fullWidth
          size="medium"
          data-vy="upsert-credit-type-units-input"
          {...register('units')}
          error={errors.units !== undefined}
          helperText={errors.units?.message}
        />
        <Controller
          control={control}
          name="isTaxable"
          defaultValue={true}
          render={({ field }) => (
            <VySwitch
              checked={field.value ?? true}
              onChange={(_, checked) => field.onChange(checked)}
              label="Tax on this product"
            />
          )}
        />
      </Stack>
    </VyDialog>
  );
};

const UpsertCreditTypeDialogWithSuspense = withSuspenseBoundary(UpsertCreditTypeDialog);

export { UpsertCreditTypeDialogWithSuspense as UpsertCreditTypeDialog };
